/**
 * @file Enemy finite state machine (GDD §9, §10, Fase 3; issue #5).
 *
 * Idle → Seek → Attack | Ranged → Dead is the original melee/ranged brain. On
 * top of it sits the `enemy_suicide_bomber` machine (issue #5):
 *
 *   Idle_Static | Idle_Roaming | Idle_Patrol → Aggro_Charge → Exploding → Dead
 *
 * Every state is one entry in the handler table, so a new behaviour never
 * touches an existing one — the same extensibility the GDD asks for (§23). All
 * of the bomber's tuning lives as data on {@link ../components/AIBrain.js} and
 * {@link ../components/ExplosiveTrigger.js}; only the *logic* lives here.
 *
 * Navigation note: chasing agents route around geometry with the shared
 * {@link ../ai/NavGrid.js NavGrid} (issue #65). With a clear line of sight the
 * body still steers straight at its target — cheaper, and the common case in
 * open rooms — but once a wall sits between them it follows an A* path around
 * it, and the collision system slides it along the rest. When no route exists
 * (unreachable target, a gap too tight for the body) it falls back to the old
 * stuck-recovery sidestep; it never teleports or passes through geometry. The
 * grid is absent in headless tests, where every chase degrades cleanly to the
 * original straight-line steering.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Velocity,
  Collider,
  AIBrain,
  AIState,
  IDLE_STATES,
  PlayerControlled,
  Health,
  Weapon,
  Explosive,
  ExplosiveTrigger,
  ThrownWeapon
} from '../components/index.js';
import { Events } from '../core/EventBus.js';
import { PALETTE } from '../config/Palette.js';
import { raycastAABB } from '../physics/AABB.js';
import { navGridForArena, PATHS_PER_FRAME } from '../ai/sharedNav.js';
import { dampAngle, distanceSqXZ, randomRange, lerp, TAU } from '../utils/MathUtils.js';

/** How fast a classic agent turns toward its heading. */
const TURN_RATE = 9;
/** Seconds between strafe direction flips for ranged agents. */
const STRAFE_FLIP_INTERVAL = 2.2;
/** Attempts made to find a navigable idle destination before giving up. */
const IDLE_DESTINATION_TRIES = 6;

export class EnemyAISystem extends System {
  static priority = 20;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /**
     * Static-collider AABBs, rebuilt each frame for line-of-sight and idle
     * destination checks. Kept as a flat scratch list to stay allocation-light.
     * @type {{ minX: number, minZ: number, maxX: number, maxZ: number }[]}
     * @private
     */
    this._walls = [];
    /** @type {number} Live count of valid entries in {@link _walls}. @private */
    this._wallCount = 0;
    /** @type {{ x: number, z: number }} Reused outward direction of a blast. @private */
    this._blastDirection = { x: 0, z: 0 };
    /** @type {{ x: number, z: number }} Reused direction of a melee shove. @private */
    this._meleeDamageDirection = { x: 0, z: 0 };
    /**
     * Reused blast-damage payload. A detonation hits every body in radius, so it
     * follows the same scratch-object rule as the other damage emitters.
     * @type {object} @private
     */
    this._blastEvent = {
      target: null,
      amount: 0,
      source: null,
      point: null,
      direction: this._blastDirection,
      impulse: 0
    };

    /**
     * The arena's shared navigation grid, resolved once per frame. Null when no
     * navigable arena exists (headless tests, pre-level), which routes every
     * chase back to straight-line steering.
     * @type {import('../ai/NavGrid.js').NavGrid | null}
     * @private
     */
    this._nav = null;
    /**
     * A* runs this system may start this frame. Independent of the ranged
     * system's identical counter — see {@link ../ai/sharedNav.js PATHS_PER_FRAME}.
     * @type {number}
     * @private
     */
    this._pathBudget = 0;

    /**
     * State handlers. Each returns the next state name, or its own to stay.
     * @type {Record<string, (context: AgentContext) => string>}
     * @private
     */
    this._states = {
      [AIState.IDLE]: (agent) =>
        agent.hasTarget && agent.distance <= agent.brain.seekRange ? AIState.SEEK : AIState.IDLE,

      [AIState.SEEK]: (agent) => {
        if (!agent.hasTarget) return AIState.IDLE;
        if (this._shouldRetreat(agent)) return AIState.RETREAT;
        const { brain } = agent;
        const perceived = this._perceive(agent);

        // A leader that can see the player narrates the hunt: every packmate
        // in earshot gets the fresh position, whether or not it can see.
        if (perceived && brain.leader) this._shareIntel(agent);

        // Contact needs current knowledge: no melee lunges through walls at a
        // position the agent cannot actually perceive.
        if (perceived && agent.distance <= brain.attackRange) {
          return brain.behavior === 'ranged' ? AIState.RANGED : AIState.ATTACK;
        }

        this._approach(agent, perceived);

        // Arrived where the player was and nothing there: start sweeping.
        if (!perceived && this._nearPoint(agent, brain.lastKnownX, brain.lastKnownZ, 1.4)) {
          brain.searchTimer = brain.searchSeconds;
          this._clearNav(brain);
          return AIState.SEARCH;
        }
        return AIState.SEEK;
      },

      [AIState.SEARCH]: (agent) => {
        if (!agent.hasTarget) return AIState.IDLE;
        const { brain } = agent;
        if (this._perceive(agent)) {
          this._clearNav(brain);
          return AIState.SEEK;
        }
        brain.searchTimer -= agent.dt;
        if (brain.searchTimer <= 0) {
          this._clearNav(brain);
          return AIState.RETURN_HOME;
        }
        // Sweep the last known position at an alert trot, leashed to it.
        brain.idleSelectTimer -= agent.dt;
        if (!brain.hasNavTarget || brain.idleSelectTimer <= 0 || this._reachedNavTarget(agent)) {
          this._pickIdleDestination(
            agent,
            brain.lastKnownX, brain.lastKnownZ, brain.searchRadius,
            brain.lastKnownX, brain.lastKnownZ, brain.searchRadius
          );
          brain.idleSelectTimer = 1.2;
        }
        this._walkToNavTarget(agent, brain.idleWalkSpeed * 1.5);
        return AIState.SEARCH;
      },

      [AIState.RETURN_HOME]: (agent) => {
        const { brain } = agent;
        if (agent.hasTarget && this._perceive(agent)) {
          this._clearNav(brain);
          return AIState.SEEK;
        }
        // The walk home is also the recovery: an abandoned hunt must not leave
        // a room full of half-dead sentries for the player to farm risk-free.
        const health = agent.entity.get(Health);
        if (health && !health.dead) {
          health.current = Math.min(health.max, health.current + health.max * brain.homeHealRate * agent.dt);
        }
        if (this._nearPoint(agent, brain.spawnX, brain.spawnZ, 1.2)) {
          if (health) health.current = health.max;
          this._clearNav(brain);
          return brain.homeState;
        }
        if (this._nav) {
          this._navChase(agent, brain.spawnX, brain.spawnZ);
          this._steerToPoint(agent, brain.navTargetX, brain.navTargetZ, 0.7);
        } else {
          this._steerToPoint(agent, brain.spawnX, brain.spawnZ, 0.7);
        }
        return AIState.RETURN_HOME;
      },

      [AIState.RETREAT]: (agent) => {
        const { brain } = agent;
        brain.retreatTimer -= agent.dt;
        if (!agent.hasTarget || brain.retreatTimer <= 0) {
          this._clearNav(brain);
          return agent.hasTarget ? AIState.SEEK : AIState.IDLE;
        }
        this._fallBack(agent);
        return AIState.RETREAT;
      },

      [AIState.ATTACK]: (agent) => {
        if (!agent.hasTarget) return AIState.IDLE;
        if (this._shouldRetreat(agent)) return AIState.RETREAT;
        // A dodge behind a pillar mid-swing breaks the engagement.
        if (!this._perceive(agent)) return AIState.SEEK;
        // Small hysteresis stops agents flickering between seek and attack.
        if (agent.distance > agent.brain.attackRange * 1.25) return AIState.SEEK;
        this._brake(agent);
        this._tryAttack(agent);
        return AIState.ATTACK;
      },

      [AIState.RANGED]: (agent) => {
        if (!agent.hasTarget) return AIState.IDLE;
        if (this._shouldRetreat(agent)) return AIState.RETREAT;
        // No target picture, no trigger: shooters re-position instead of
        // hosing a wall the player ducked behind.
        if (!this._perceive(agent)) return AIState.SEEK;
        if (agent.distance > agent.brain.attackRange * 1.15) return AIState.SEEK;
        this._holdDistance(agent);
        this._faceTarget(agent);
        this._setTrigger(agent, true);
        return AIState.RANGED;
      },

      // --- Issue #5: suicide-bomber states ---------------------------------
      [AIState.IDLE_STATIC]: (agent) => this._idleStatic(agent),
      [AIState.IDLE_ROAMING]: (agent) => this._idleRoaming(agent),
      [AIState.IDLE_PATROL]: (agent) => this._idlePatrol(agent),
      [AIState.AGGRO_CHARGE]: (agent) => this._aggroCharge(agent),
      [AIState.EXPLODING]: (agent) => this._exploding(agent),

      [AIState.STUNNED]: (agent) => {
        const { brain } = agent;
        brain.stunRemaining = Math.max(0, brain.stunRemaining - agent.dt);
        // A hard stop, not a slowdown: the stun is the player's window.
        this._brake(agent);
        agent.velocity.linear.set(0, 0, 0);
        this._emitStunStars(agent);
        // Resume the archetype's own idle/aggro state, not the generic IDLE:
        // a bomber woken back to plain IDLE never re-enters its vision cone
        // (see `_states[AIState.IDLE]`) and stays permanently defused.
        return brain.stunRemaining > 0 ? AIState.STUNNED : brain.homeState;
      },

      [AIState.SEEK_WEAPON]: (agent) => {
        const drop = this._claimWeapon(agent);
        if (!drop) return AIState.IDLE;

        const dropPosition = drop.get(Transform).position;
        const reach = this.ctx.config.throwing.enemyPickupRadius;
        const distanceSq = distanceSqXZ(
          agent.transform.position.x,
          agent.transform.position.z,
          dropPosition.x,
          dropPosition.z
        );

        if (distanceSq <= reach * reach) {
          this._equip(agent, drop);
          return AIState.IDLE;
        }

        this._steerTo(agent, dropPosition, 1);
        return AIState.SEEK_WEAPON;
      },

      [AIState.DEAD]: () => AIState.DEAD
    };

    /** @type {THREE.Vector3} Scratch for stun-star emission. @private */
    this._starPosition = new THREE.Vector3();
    /** @type {THREE.Vector3} Scratch for stun-star emission. @private */
    this._starDirection = new THREE.Vector3();
    /** @type {{ x: number, z: number }} Direction of the current melee shove. @private */
    this._meleeDamageDirection = { x: 0, z: 0 };
    /** @type {object} Reused FX payload. @private */
    this._starEvent = {
      position: this._starPosition,
      direction: this._starDirection,
      count: 1,
      options: {
        speed: [1.8, 2.2],
        spread: 0.08,
        life: [0.34, 0.42],
        size: [0.09, 0.15],
        color: PALETTE.stunStar
      }
    };
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DAMAGE, (payload) => this._onDamage(payload)),
      // Sound is the second sense (AI overhaul): gunfire hands out positions.
      this.ctx.events.on(Events.WEAPON_FIRED, (payload) => this._onGunshot(payload)),
      // Death routes the bomber blast, honouring explode_on_death (issue #5).
      this.ctx.events.on(Events.DIED, ({ entity }) => {
        this._onDied(entity);
        const brain = entity.get(AIBrain);
        if (brain?.leader) this._panicPack(entity, brain);
      }),
      // Contact aggro (issue #18): only idle enemies enter aggro on contact.
      // Already-aggro'd states (SEEK / ATTACK / RANGED) must not be forced
      // back to SEEK every frame while the player remains overlapping — that
      // thrashes combat and resets timeInState repeatedly.
      this.ctx.events.on(Events.ENEMY_CONTACT, ({ enemy }) => {
        if (!enemy?.alive) return;
        const brain = enemy.get(AIBrain);
        if (!brain || brain.state !== AIState.IDLE) return;
        this._transition(enemy, brain, AIState.SEEK);
      })
    );
  }

  /**
   * @typedef {object} AgentContext
   * @property {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @property {import('../components/AIBrain.js').AIBrain} brain Agent brain.
   * @property {import('../components/Transform.js').Transform} transform Agent transform.
   * @property {import('../components/Velocity.js').Velocity} velocity Agent velocity.
   * @property {import('../ecs/Entity.js').Entity | null} target Live player, if any.
   * @property {import('../components/Transform.js').Transform | null} targetTransform Target transform.
   * @property {boolean} hasTarget Whether a live target exists.
   * @property {number} distance Planar distance to the target.
   * @property {number} dt Delta time in seconds.
   */

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const agents = this.world.query(AIBrain, Transform, Velocity);
    // Flanking is a pack move: a lone chaser runs straight (also what the
    // no-grid fallback contract expects), so count this frame's hunters once.
    this._seekerCount = 0;
    for (const entity of agents) {
      if (entity.get(AIBrain).state === AIState.SEEK) this._seekerCount++;
    }
    if (agents.length === 0) return;

    this._rebuildWalls();

    // Resolve the shared nav grid once and refill this frame's A* budget. Both
    // are cheap and null-safe: without an arena the chase logic steers directly.
    this._nav = navGridForArena(this.ctx.arena, this.ctx.config?.dungeon?.wallHeight ?? 0);
    this._pathBudget = PATHS_PER_FRAME;

    const target = this._findTarget();
    const targetTransform = target ? target.get(Transform) : null;

    /** @type {AgentContext} Reused to keep the loop allocation-free. */
    const agent = {
      entity: null,
      brain: null,
      transform: null,
      velocity: null,
      target,
      targetTransform,
      hasTarget: Boolean(target),
      distance: Infinity,
      dt
    };

    for (const entity of agents) {
      if (!entity.alive) continue;
      agent.entity = entity;
      agent.brain = entity.get(AIBrain);
      agent.transform = entity.get(Transform);
      agent.velocity = entity.get(Velocity);
      agent.brain.target = target;

      agent.distance = targetTransform
        ? Math.sqrt(
            distanceSqXZ(
              agent.transform.position.x,
              agent.transform.position.z,
              targetTransform.position.x,
              targetTransform.position.z
            )
          )
        : Infinity;

      agent.brain.timeInState += dt;
      agent.brain.attackCooldown = Math.max(0, agent.brain.attackCooldown - dt);
      agent.brain.meleeSwingRemaining = Math.max(0, agent.brain.meleeSwingRemaining - dt);
      if (agent.brain.detectionTimer > 0) agent.brain.detectionTimer -= dt;

      // Two interrupts that outrank whatever the agent was doing. Being stunned
      // wins outright; scavenging only kicks in once the stun has worn off,
      // which is what keeps a stunned enemy from crawling after a weapon.
      if (agent.brain.stunRemaining > 0) {
        agent.brain.setState(AIState.STUNNED);
      } else if (this._needsWeapon(agent)) {
        agent.brain.setState(AIState.SEEK_WEAPON);
      }

      // Default every frame: idle agents stop, and nobody fires unless the
      // active state explicitly asks for it.
      if (!agent.hasTarget) this._brake(agent);
      this._setTrigger(agent, false);

      const previous = agent.brain.state;
      const next = this._states[previous]?.(agent) ?? AIState.IDLE;

      if (next !== previous) {
        this._onExitState(agent, previous);
        this._transition(entity, agent.brain, next);
        this._onEnterState(agent, next);
        this.ctx.events.emit(Events.ENEMY_STATE_CHANGED, { entity, previous, next });
      }

      // A handler may have detonated (and destroyed) the agent this frame.
      if (!entity.alive) continue;

      // Stunned agents are a hard stop: separation must not re-fill `desired`
      // after the stun handler zeroed it, or Movement would slide them along.
      if (agent.brain.state === AIState.STUNNED) continue;

      // Spec: while fused the body must not keep moving. Separation would
      // rewrite `desired` after the explode handler zeroed it, and with
      // maxSpeed === 0 the movement system skips its speed clamp — so skip
      // the push entirely for EXPLODING agents.
      if (agent.brain.state !== AIState.EXPLODING) {
        this._applySeparation(agent, agents);
      }
      if (this._usesManagedRotation(agent.brain.state)) {
        // Bomber states steer their own facing; skip the generic pass.
      } else if (agent.brain.state !== AIState.RANGED) {
        this._faceMotion(agent);
      }
      // The bomber's damage flag is consumed once per frame after dispatch.
      agent.brain.wasDamaged = false;
    }
  }

  // ------------------------------------------------------------ bomber: idle

  /**
   * Idle_Static: hold spawn position and facing, watch the cone.
   * @param {AgentContext} agent Current agent.
   * @returns {string}
   * @private
   */
  _idleStatic(agent) {
    agent.velocity.maxSpeed = 0;
    this._brake(agent);
    agent.transform.rotationY = agent.brain.spawnRotationY;
    return this._detect(agent) ? this._aggroEntry(agent.entity) : AIState.IDLE_STATIC;
  }

  /**
   * Idle_Roaming: wander inside a radius around the spawn point.
   * @param {AgentContext} agent Current agent.
   * @returns {string}
   * @private
   */
  _idleRoaming(agent) {
    if (this._detect(agent)) return this._aggroEntry(agent.entity);
    const { brain } = agent;
    brain.idleSelectTimer -= agent.dt;

    if (!brain.hasNavTarget || brain.idleSelectTimer <= 0 || this._reachedNavTarget(agent)) {
      this._pickIdleDestination(agent, brain.spawnX, brain.spawnZ, brain.idleRoamingRange, brain.spawnX, brain.spawnZ, brain.idleRoamingRange);
      brain.idleSelectTimer = brain.idleRoamingSelectSeconds;
    }
    this._walkToNavTarget(agent, brain.idleWalkSpeed);
    return AIState.IDLE_ROAMING;
  }

  /**
   * Idle_Patrol: pick destinations relative to the current position so the
   * agent drifts across the map, optionally leashed to its spawn.
   * @param {AgentContext} agent Current agent.
   * @returns {string}
   * @private
   */
  _idlePatrol(agent) {
    if (this._detect(agent)) return this._aggroEntry(agent.entity);
    const { brain } = agent;
    brain.idleSelectTimer -= agent.dt;

    if (!brain.hasNavTarget || brain.idleSelectTimer <= 0 || this._reachedNavTarget(agent)) {
      const leash = brain.idlePatrolMaxDistanceFromSpawn > 0 ? brain.idlePatrolMaxDistanceFromSpawn : Infinity;
      this._pickIdleDestination(
        agent,
        agent.transform.position.x,
        agent.transform.position.z,
        brain.idlePatrolRange,
        brain.spawnX,
        brain.spawnZ,
        leash
      );
      brain.idleSelectTimer = brain.idlePatrolSelectSeconds;
    }
    this._walkToNavTarget(agent, brain.idleWalkSpeed);
    return AIState.IDLE_PATROL;
  }

  // ---------------------------------------------------------- bomber: charge

  /**
   * Aggro_Charge: run down the player's *current* position with periodic
   * re-targeting, acceleration, managed rotation and stuck recovery, then hand
   * off to Exploding on contact/proximity.
   * @param {AgentContext} agent Current agent.
   * @returns {string}
   * @private
   */
  _aggroCharge(agent) {
    const { brain } = agent;

    // Target still valid? A dead/absent player ends the charge cleanly.
    if (!agent.hasTarget) {
      this._clearNav(brain);
      return brain.invalidTargetReturnState;
    }

    const px = agent.targetTransform.position.x;
    const pz = agent.targetTransform.position.z;

    // Line of sight feeds both the memory timer and last-known position.
    const canSee = this._hasLineOfSight(agent.transform.position.x, agent.transform.position.z, px, pz);
    if (canSee) {
      brain.hasLineOfSight = true;
      brain.timeSinceSeen = 0;
      brain.lastKnownX = px;
      brain.lastKnownZ = pz;
    } else {
      brain.hasLineOfSight = false;
      brain.timeSinceSeen += agent.dt;
      if (brain.aggroCanLoseTarget && brain.timeSinceSeen >= brain.aggroTargetMemorySeconds) {
        this._clearNav(brain);
        return brain.aggroLostTargetReturnState;
      }
    }

    // Chase the live player when we can see it (or when we never give up);
    // otherwise chase the last place we saw it.
    const chaseX = canSee || !brain.aggroCanLoseTarget ? px : brain.lastKnownX;
    const chaseZ = canSee || !brain.aggroCanLoseTarget ? pz : brain.lastKnownZ;

    // Detonate on contact or proximity — the single arming transition.
    const trigger = agent.entity.get(ExplosiveTrigger);
    if (trigger && agent.distance <= trigger.triggerDistance) {
      return AIState.EXPLODING;
    }

    this._navChase(agent, chaseX, chaseZ);

    // Acceleration ramp (optional), else the flat charge speed.
    if (brain.aggroAccelerationEnabled) {
      brain.currentChargeSpeed = Math.min(
        brain.aggroMaxSpeed,
        brain.currentChargeSpeed + brain.aggroAccelerationPerSecond * agent.dt
      );
    } else {
      brain.currentChargeSpeed = brain.aggroChargeSpeed;
    }

    this._steerToNavTarget(agent, brain.currentChargeSpeed, brain.aggroRotationSpeed);
    return AIState.AGGRO_CHARGE;
  }

  /**
   * Exploding: locked, braked, counting down the fuse. When it elapses the
   * agent kills itself so death flows through the normal DIED path (score,
   * loot, wave tracking) and the blast is applied by {@link _onDied}.
   * @param {AgentContext} agent Current agent.
   * @returns {string}
   * @private
   */
  _exploding(agent) {
    const trigger = agent.entity.get(ExplosiveTrigger);
    // Hard stop: residual linear velocity would otherwise coast under maxSpeed 0
    // (the movement clamp only runs when maxSpeed > 0), and the fuse must not
    // let the body keep sliding (issue #5).
    agent.velocity.desired.set(0, 0, 0);
    agent.velocity.linear.set(0, 0, 0);
    agent.velocity.maxSpeed = 0;

    if (!trigger) return AIState.EXPLODING;

    trigger.fuseRemaining -= agent.dt;
    if (trigger.fuseRemaining <= 0) {
      // Clear any lingering i-frames so the self-kill always lands.
      const health = agent.entity.get(Health);
      if (health) health.invulnerableFor = 0;
      this.ctx.events.emit(Events.DAMAGE, {
        target: agent.entity,
        amount: health ? health.max : 1,
        source: agent.entity,
        point: agent.transform.position
      });
    }
    return AIState.EXPLODING;
  }

  // ---------------------------------------------------------- enter/exit

  /**
   * @param {AgentContext} agent Current agent.
   * @param {string} state State being entered.
   * @returns {void}
   * @private
   */
  _onEnterState(agent, state) {
    const { brain } = agent;
    switch (state) {
      case AIState.AGGRO_CHARGE:
        this._clearNav(brain);
        brain.currentChargeSpeed = brain.aggroAccelerationEnabled ? brain.aggroInitialSpeed : brain.aggroChargeSpeed;
        brain.timeSinceSeen = 0;
        brain.stuckTimer = 0;
        brain.isStuck = false;
        brain.stuckSampleX = agent.transform.position.x;
        brain.stuckSampleZ = agent.transform.position.z;
        brain.repathTimer = 0;
        this.ctx.events.emit(Events.ENEMY_CHARGE_STARTED, { entity: agent.entity, target: brain.target });
        break;
      case AIState.EXPLODING: {
        const trigger = agent.entity.get(ExplosiveTrigger);
        if (trigger && !trigger.hasStartedExploding) {
          trigger.hasStartedExploding = true;
          trigger.fuseRemaining = trigger.fuseSeconds;
          this.ctx.events.emit(Events.ENEMY_EXPLOSION_TRIGGERED, {
            entity: agent.entity,
            position: agent.transform.position,
            radius: trigger.radius,
            damage: trigger.damage
          });
        }
        this._clearNav(brain);
        break;
      }
      case AIState.IDLE_STATIC:
      case AIState.IDLE_ROAMING:
      case AIState.IDLE_PATROL:
        this._clearNav(brain);
        brain.idleSelectTimer = 0;
        break;
      default:
        break;
    }
  }

  /**
   * Applies a state change and announces the one edge other systems care about:
   * idle → seek, the moment the agent commits to a target. Re-entering seek
   * from attack or ranged is the same engagement continuing, not a new
   * detection, so it stays silent — otherwise an agent circling the player at
   * the edge of its attack range would strobe alerts.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/AIBrain.js').AIBrain} brain Agent brain.
   * @param {string} next One of {@link AIState}.
   * @returns {void}
   * @private
   */
  _transition(entity, brain, next) {
    if (brain.state === next) return;
    const wasIdle = IDLE_STATES.includes(brain.state) || brain.state === AIState.IDLE;
    brain.setState(next);
    if (wasIdle && (next === AIState.SEEK || next === AIState.AGGRO_CHARGE)) {
      this.ctx.events.emit(Events.ENEMY_AGGRO, { entity });
      this._shout(entity, brain);
    }
  }

  /**
   * What the hunter actually knows. Sight is a throttled line-of-sight check —
   * walls end it, range is the seek range — and every confirmed sighting
   * refreshes the last known position. Everything the chase does steers at
   * that memory, never at the live transform, so the player can genuinely
   * break contact.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True while the agent can currently see the player.
   * @private
   */
  _perceive(agent) {
    const { brain } = agent;
    if (!agent.hasTarget) return false;

    brain.perceptionTimer -= agent.dt;
    if (brain.perceptionTimer > 0) return brain.hasLineOfSight;
    brain.perceptionTimer = brain.detectionCheckSeconds;

    const px = agent.targetTransform.position.x;
    const pz = agent.targetTransform.position.z;
    const seen =
      agent.distance <= brain.seekRange &&
      this._hasLineOfSight(agent.transform.position.x, agent.transform.position.z, px, pz);

    brain.hasLineOfSight = seen;
    if (seen) {
      brain.timeSinceSeen = 0;
      brain.lastKnownX = px;
      brain.lastKnownZ = pz;
    } else {
      brain.timeSinceSeen += brain.detectionCheckSeconds;
    }
    return seen;
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @param {number} x Point X.
   * @param {number} z Point Z.
   * @param {number} tolerance Arrival distance.
   * @returns {boolean}
   * @private
   */
  _nearPoint(agent, x, z, tolerance) {
    return distanceSqXZ(agent.transform.position.x, agent.transform.position.z, x, z) <= tolerance * tolerance;
  }

  /**
   * A perceiving leader feeds its position fix to every hunting or searching
   * packmate in earshot. Intel, not teleporting: listeners still walk to the
   * position and can still be wrong by the time they arrive.
   * @param {AgentContext} agent Leader agent.
   * @returns {void}
   * @private
   */
  _shareIntel(agent) {
    const { brain } = agent;
    if (brain.alertRadius <= 0) return;
    const radiusSq = brain.alertRadius * brain.alertRadius;
    const ax = agent.transform.position.x;
    const az = agent.transform.position.z;

    for (const other of this.world.query(AIBrain, Transform)) {
      if (other === agent.entity || !other.alive) continue;
      const ob = other.get(AIBrain);
      if (ob.state !== AIState.SEEK && ob.state !== AIState.SEARCH) continue;
      const ot = other.get(Transform);
      if (distanceSqXZ(ot.position.x, ot.position.z, ax, az) > radiusSq) continue;
      ob.lastKnownX = brain.lastKnownX;
      ob.lastKnownZ = brain.lastKnownZ;
      ob.timeSinceSeen = 0;
    }
  }

  /**
   * The player's gunfire is a position broadcast: idle agents in earshot go
   * and INVESTIGATE the sound (search, not seek — they heard, they did not
   * see), hunters and searchers re-localise their memory. Dormant bombers
   * ignore noise: a mine that investigates is a mine defused for free.
   *
   * @param {{ position: { x: number, z: number }, owner: import('../ecs/Entity.js').Entity }} payload
   * @returns {void}
   * @private
   */
  _onGunshot({ position, owner }) {
    if (!owner?.has?.(PlayerControlled) || !position) return;

    for (const entity of this.world.query(AIBrain, Transform)) {
      if (!entity.alive || entity.has(ExplosiveTrigger)) continue;
      const brain = entity.get(AIBrain);
      const at = entity.get(Transform);
      const distSq = distanceSqXZ(at.position.x, at.position.z, position.x, position.z);
      if (distSq > brain.hearingRadius * brain.hearingRadius) continue;

      const idle = IDLE_STATES.includes(brain.state) || brain.state === AIState.IDLE;
      if (idle) {
        brain.lastKnownX = position.x;
        brain.lastKnownZ = position.z;
        brain.searchTimer = brain.searchSeconds;
        this._transition(entity, brain, AIState.SEARCH);
      } else if (brain.state === AIState.SEEK || brain.state === AIState.SEARCH) {
        brain.lastKnownX = position.x;
        brain.lastKnownZ = position.z;
        brain.timeSinceSeen = 0;
        if (brain.state === AIState.SEARCH) brain.searchTimer = brain.searchSeconds;
      }
    }
  }

  /**
   * The pack watched its leader die: every nearby melee hunter breaks and
   * runs for a beat. Panic is not the once-per-life retreat — it is a group
   * reaction, and it can happen to archetypes that never retreat on health.
   * @param {import('../ecs/Entity.js').Entity} leader The fallen leader.
   * @param {import('../components/AIBrain.js').AIBrain} brain Its brain.
   * @returns {void}
   * @private
   */
  _panicPack(leader, brain) {
    const at = leader.get(Transform);
    if (!at || brain.alertRadius <= 0) return;
    const radiusSq = brain.alertRadius * brain.alertRadius;

    for (const other of this.world.query(AIBrain, Transform)) {
      if (other === leader || !other.alive || other.has(ExplosiveTrigger)) continue;
      const ob = other.get(AIBrain);
      if (ob.leader) continue;
      if (ob.state !== AIState.SEEK && ob.state !== AIState.ATTACK && ob.state !== AIState.SEARCH) continue;
      const ot = other.get(Transform);
      if (distanceSqXZ(ot.position.x, ot.position.z, at.position.x, at.position.z) > radiusSq) continue;
      ob.retreatTimer = ob.retreatSeconds;
      this._clearNav(ob);
      ob.setState(AIState.RETREAT);
    }
  }

  /**
   * The state an idle agent enters on detection. It derives from what the
   * entity IS, not from config: anything carrying an ExplosiveTrigger charges
   * to detonate; everything else hunts.
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @returns {string}
   * @private
   */
  _aggroEntry(entity) {
    return entity.has(ExplosiveTrigger) ? AIState.AGGRO_CHARGE : AIState.SEEK;
  }

  /**
   * The aggro shout: an agent that just noticed the player wakes every idle
   * ally inside its alert radius, so a room engages as a pack instead of one
   * straggler at a time. The cascade is bounded — each listener leaves the
   * idle set as it wakes, so no agent shouts twice.
   *
   * @param {import('../ecs/Entity.js').Entity} shouter Agent that aggroed.
   * @param {import('../components/AIBrain.js').AIBrain} brain Its brain.
   * @returns {void}
   * @private
   */
  _shout(shouter, brain) {
    if (brain.alertRadius <= 0) return;
    const at = shouter.get(Transform);
    if (!at) return;
    const radiusSq = brain.alertRadius * brain.alertRadius;

    for (const other of this.world.query(AIBrain, Transform)) {
      if (other === shouter || !other.alive) continue;
      const ob = other.get(AIBrain);
      if (!IDLE_STATES.includes(ob.state) && ob.state !== AIState.IDLE) continue;
      const ot = other.get(Transform);
      const dx = ot.position.x - at.position.x;
      const dz = ot.position.z - at.position.z;
      if (dx * dx + dz * dz > radiusSq) continue;
      this._transition(other, ob, this._aggroEntry(other));
    }
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @param {string} _state State being left.
   * @returns {void}
   * @private
   */
  _onExitState(agent, _state) {
    // Idle nav intent never survives a state change.
    this._clearNav(agent.brain);
  }

  // ---------------------------------------------------------- detection

  /**
   * Vision-cone detection for the idle states: range, angle and line of sight.
   * Throttled by `enemy_detection_check_seconds`. A pending damage flag also
   * trips detection, so a shot from behind still wakes the bomber.
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when the player is (now or already) acquired.
   * @private
   */
  _detect(agent) {
    const { brain } = agent;

    if (brain.wasDamaged && agent.hasTarget) {
      this._acquire(agent);
      return true;
    }
    if (!agent.hasTarget || brain.detectionTimer > 0) return false;
    brain.detectionTimer = brain.detectionCheckSeconds;

    const fromX = agent.transform.position.x;
    const fromZ = agent.transform.position.z;
    const px = agent.targetTransform.position.x;
    const pz = agent.targetTransform.position.z;

    const dx = px - fromX;
    const dz = pz - fromZ;
    const distSq = dx * dx + dz * dz;
    if (distSq > brain.detectionConeRange * brain.detectionConeRange) return false;

    // Angle: compare the heading to the direction toward the player.
    const facing = agent.transform.rotationY;
    const toAngle = Math.atan2(dx, dz);
    let delta = Math.abs(toAngle - facing) % TAU;
    if (delta > Math.PI) delta = TAU - delta;
    if (delta > brain.detectionConeHalfAngle) return false;

    if (this._hasLineOfSight(fromX, fromZ, px, pz)) {
      this._acquire(agent);
      return true;
    }
    return false;
  }

  /**
   * Commits the agent to the player: records the sighting and announces it.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _acquire(agent) {
    const { brain } = agent;
    brain.lastKnownX = agent.targetTransform.position.x;
    brain.lastKnownZ = agent.targetTransform.position.z;
    brain.timeSinceSeen = 0;
    this._clearNav(brain);
    this.ctx.events.emit(Events.ENEMY_DETECTED_PLAYER, { entity: agent.entity, target: agent.target });
  }

  // ---------------------------------------------------------- navigation

  /**
   * Re-aims the steering target at the chase point when the re-path timer
   * elapses, the target has moved far enough, or the agent is wedged.
   * @param {AgentContext} agent Current agent.
   * @param {number} chaseX Desired X.
   * @param {number} chaseZ Desired Z.
   * @returns {void}
   * @private
   */
  _maybeRepath(agent, chaseX, chaseZ) {
    const { brain } = agent;
    brain.repathTimer -= agent.dt;

    const moved = distanceSqXZ(brain.pathPlannedForX, brain.pathPlannedForZ, chaseX, chaseZ);
    const movedEnough = moved > brain.aggroRepathMovementThreshold * brain.aggroRepathMovementThreshold;

    if (!brain.hasNavTarget || brain.repathTimer <= 0 || movedEnough || brain.isStuck) {
      brain.repathTimer = brain.aggroRepathSeconds;
      brain.pathPlannedForX = chaseX;
      brain.pathPlannedForZ = chaseZ;

      if (brain.isStuck) {
        // Sidestep: aim at a point offset perpendicular to the target line so
        // steering peels the body off the obstacle instead of grinding it.
        const dx = chaseX - agent.transform.position.x;
        const dz = chaseZ - agent.transform.position.z;
        const len = Math.hypot(dx, dz) || 1;
        const side = Math.random() < 0.5 ? 1 : -1;
        brain.navTargetX = chaseX + (-dz / len) * brain.navStuckDistance * 6 * side;
        brain.navTargetZ = chaseZ + (dx / len) * brain.navStuckDistance * 6 * side;
        brain.isStuck = false;
        brain.stuckTimer = 0;
      } else {
        brain.navTargetX = chaseX;
        brain.navTargetZ = chaseZ;
      }
      brain.hasNavTarget = true;
    }
  }

  /**
   * Flags the agent as stuck when it makes almost no progress over a window,
   * which lets {@link _maybeRepath} peel it off an obstacle.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateStuck(agent) {
    const { brain } = agent;
    brain.stuckTimer += agent.dt;
    if (brain.stuckTimer < brain.navStuckSeconds) return;

    const moved = Math.sqrt(
      distanceSqXZ(brain.stuckSampleX, brain.stuckSampleZ, agent.transform.position.x, agent.transform.position.z)
    );
    brain.isStuck = moved < brain.navStuckDistance;
    brain.stuckTimer = 0;
    brain.stuckSampleX = agent.transform.position.x;
    brain.stuckSampleZ = agent.transform.position.z;
  }

  /**
   * Approach the player. With a nav grid, chases around walls (see
   * {@link _navChase}); without one, plain straight-line steering, exactly as
   * before pathfinding existed. Rotation is left to {@link _faceMotion}, which
   * turns the body toward its desired velocity.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _approach(agent, perceived = true) {
    // The chase steers at the agent's MEMORY of the player, refreshed only by
    // perception — never at the live transform.
    const tx = agent.brain.lastKnownX;
    const tz = agent.brain.lastKnownZ;
    let goalX = tx, goalZ = tz;
    // Flanking: each chaser aims at a personal point on an arc around the
    // target, spread by a stable per-entity bearing, so a pack surrounds
    // instead of forming a conga line. Only while the target is actually in
    // sight — an agent chasing a memory beelines for it.
    const spread = Math.min(agent.distance * 0.45, 5);
    if (perceived && this._seekerCount > 1 && spread > agent.brain.attackRange * 0.8) {
      const bearing = Math.atan2(agent.transform.position.x - tx, agent.transform.position.z - tz);
      // Golden-angle hash of the entity id: stable, cheap, well distributed.
      const offset = ((agent.entity.id * 2.399963) % (Math.PI * 0.9)) - Math.PI * 0.45;
      goalX = tx + Math.sin(bearing + offset) * spread;
      goalZ = tz + Math.cos(bearing + offset) * spread;
    }

    if (!this._nav) {
      this._steerToPoint(agent, goalX, goalZ, 1);
      return;
    }
    const { brain } = agent;
    this._navChase(agent, goalX, goalZ);
    this._steerToPoint(agent, brain.navTargetX, brain.navTargetZ, 1);
  }

  /**
   * Whether a mauled agent should break off. Once per life, data-driven: only
   * archetypes with a retreat ratio ever blink, and a retreat is committed the
   * moment it starts (the latch flips here, not on completion).
   * @param {AgentContext} agent Current agent.
   * @returns {boolean}
   * @private
   */
  _shouldRetreat(agent) {
    const { brain } = agent;
    if (brain.retreatHealthRatio <= 0 || brain.hasRetreated) return false;
    const health = agent.entity.get(Health);
    if (!health || health.ratio > brain.retreatHealthRatio) return false;

    // Morale: nobody breaks while the pack leader is standing close enough to
    // see it happen. Kill the brute first and THEN the grunts blink.
    for (const other of this.world.query(AIBrain, Transform)) {
      if (other === agent.entity || !other.alive) continue;
      const ob = other.get(AIBrain);
      if (!ob.leader || ob.state === AIState.DEAD) continue;
      const ot = other.get(Transform);
      const dx = ot.position.x - agent.transform.position.x;
      const dz = ot.position.z - agent.transform.position.z;
      if (dx * dx + dz * dz <= ob.alertRadius * ob.alertRadius) return false;
    }

    brain.hasRetreated = true;
    brain.retreatTimer = brain.retreatSeconds;
    this._clearNav(brain);
    return true;
  }

  /**
   * Runs away from the player, nav-aware, so the fallback naturally rounds
   * corners and breaks line of sight — the melee version of taking cover.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _fallBack(agent) {
    const px = agent.targetTransform.position.x;
    const pz = agent.targetTransform.position.z;
    const ax = agent.transform.position.x;
    const az = agent.transform.position.z;
    const away = Math.atan2(ax - px, az - pz);
    const goalX = ax + Math.sin(away) * 7;
    const goalZ = az + Math.cos(away) * 7;
    if (this._nav) {
      this._navChase(agent, goalX, goalZ);
      this._steerToPoint(agent, agent.brain.navTargetX, agent.brain.navTargetZ, 1);
    } else {
      this._steerToPoint(agent, goalX, goalZ, 1);
    }
  }

  /**
   * Chase `(tx, tz)` the smart way and leave the result in the agent's nav
   * target for the caller to steer at:
   *
   *  - clear line of sight → aim straight at the target (cheap, open-room case);
   *  - a wall in the way → follow an A* route around it, waypoint by waypoint;
   *  - no route *and* wedged → a perpendicular sidestep so the body never
   *    freezes against geometry;
   *  - no route but not wedged, or the frame's A* budget spent → keep aiming
   *    straight and retry next tick.
   *
   * Without a nav grid this is the pre-#65 re-aim: stuck detection plus the
   * random sidestep, byte-for-byte, so headless tests are unaffected.
   *
   * @param {AgentContext} agent Current agent.
   * @param {number} tx Chase target X.
   * @param {number} tz Chase target Z.
   * @returns {void}
   * @private
   */
  _navChase(agent, tx, tz) {
    const { brain, transform } = agent;

    if (!this._nav) {
      this._updateStuck(agent);
      this._maybeRepath(agent, tx, tz);
      return;
    }

    this._updateStuck(agent);
    brain.repathTimer -= agent.dt;

    const fromX = transform.position.x;
    const fromZ = transform.position.z;

    // Fast path: a clear straight shot needs no A* at all.
    if (!brain.isStuck && this._nav.hasLineOfSight(fromX, fromZ, tx, tz)) {
      this._clearPath(brain);
      brain.navTargetX = tx;
      brain.navTargetZ = tz;
      brain.hasNavTarget = true;
      return;
    }

    const moved = distanceSqXZ(brain.pathPlannedForX, brain.pathPlannedForZ, tx, tz);
    const movedEnough =
      moved > brain.aggroRepathMovementThreshold * brain.aggroRepathMovementThreshold;
    const noRoute = !brain.path || brain.pathIndex >= brain.path.length;
    const wantsRepath = noRoute || brain.repathTimer <= 0 || movedEnough || brain.isStuck;

    if (wantsRepath && this._pathBudget > 0) {
      this._pathBudget--;
      const path = this._nav.findPath(fromX, fromZ, tx, tz, brain.radius);
      if (path && path.length > 0) {
        brain.path = path;
        brain.pathIndex = 0;
        brain.repathTimer = brain.aggroRepathSeconds;
        brain.pathPlannedForX = tx;
        brain.pathPlannedForZ = tz;
        brain.isStuck = false;
        brain.stuckTimer = 0;
      } else if (brain.isStuck) {
        this._sidestep(agent, tx, tz);
        return;
      } else {
        this._clearPath(brain);
        brain.navTargetX = tx;
        brain.navTargetZ = tz;
        brain.hasNavTarget = true;
        return;
      }
    }

    // Steer at the current waypoint, popping any already reached.
    if (brain.path && brain.pathIndex < brain.path.length) {
      const tol = brain.navArrivalTolerance * brain.navArrivalTolerance;
      let waypoint = brain.path[brain.pathIndex];
      while (
        brain.pathIndex < brain.path.length - 1 &&
        distanceSqXZ(fromX, fromZ, waypoint.x, waypoint.z) <= tol
      ) {
        brain.pathIndex++;
        waypoint = brain.path[brain.pathIndex];
      }
      brain.navTargetX = waypoint.x;
      brain.navTargetZ = waypoint.z;
      brain.hasNavTarget = true;
    } else {
      brain.navTargetX = tx;
      brain.navTargetZ = tz;
      brain.hasNavTarget = true;
    }
  }

  /**
   * Aims at a point offset perpendicular to the target line, so steering peels
   * the body off an obstacle it could not route around. The last-resort
   * recovery when {@link _navChase} finds no path.
   * @param {AgentContext} agent Current agent.
   * @param {number} tx Chase target X.
   * @param {number} tz Chase target Z.
   * @returns {void}
   * @private
   */
  _sidestep(agent, tx, tz) {
    const { brain, transform } = agent;
    const dx = tx - transform.position.x;
    const dz = tz - transform.position.z;
    const len = Math.hypot(dx, dz) || 1;
    const side = Math.random() < 0.5 ? 1 : -1;
    brain.navTargetX = tx + (-dz / len) * brain.navStuckDistance * 6 * side;
    brain.navTargetZ = tz + (dx / len) * brain.navStuckDistance * 6 * side;
    brain.hasNavTarget = true;
    brain.isStuck = false;
    brain.stuckTimer = 0;
    this._clearPath(brain);
  }

  /**
   * Drops any active route so the next chase starts from straight steering.
   * @param {import('../components/AIBrain.js').AIBrain} brain Agent brain.
   * @returns {void}
   * @private
   */
  _clearPath(brain) {
    brain.path = null;
    brain.pathIndex = 0;
  }

  /**
   * Steers toward the current nav target at `speed`, turning the body toward the
   * heading at `rotationRate`.
   * @param {AgentContext} agent Current agent.
   * @param {number} speed Movement speed.
   * @param {number} rotationRate Turn approach rate.
   * @returns {void}
   * @private
   */
  _steerToNavTarget(agent, speed, rotationRate) {
    const { brain, velocity, transform } = agent;
    const tx = brain.hasNavTarget ? brain.navTargetX : agent.targetTransform.position.x;
    const tz = brain.hasNavTarget ? brain.navTargetZ : agent.targetTransform.position.z;
    const dx = tx - transform.position.x;
    const dz = tz - transform.position.z;
    const len = Math.hypot(dx, dz) || 1;

    velocity.maxSpeed = speed;
    velocity.desired.set((dx / len) * speed, 0, (dz / len) * speed);
    transform.rotationY = dampAngle(transform.rotationY, Math.atan2(dx, dz), rotationRate, agent.dt);
  }

  /**
   * Idle walk toward the nav target; brakes and clears intent on arrival.
   * @param {AgentContext} agent Current agent.
   * @param {number} speed Walk speed.
   * @returns {void}
   * @private
   */
  _walkToNavTarget(agent, speed) {
    const { brain, velocity } = agent;
    if (!brain.hasNavTarget) {
      velocity.maxSpeed = speed;
      this._brake(agent);
      return;
    }
    if (this._reachedNavTarget(agent)) {
      this._clearNav(brain);
      this._brake(agent);
      return;
    }
    this._steerToNavTarget(agent, speed, agent.brain.aggroRotationSpeed);
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when within arrival tolerance of the nav target.
   * @private
   */
  _reachedNavTarget(agent) {
    const { brain } = agent;
    if (!brain.hasNavTarget) return false;
    const d = distanceSqXZ(agent.transform.position.x, agent.transform.position.z, brain.navTargetX, brain.navTargetZ);
    return d <= brain.navArrivalTolerance * brain.navArrivalTolerance;
  }

  /**
   * Chooses a navigable idle destination in a disc around `(cx, cz)` that also
   * stays within `leashRange` of `(leashX, leashZ)` and clear of walls.
   * @param {AgentContext} agent Current agent.
   * @param {number} cx Disc centre X.
   * @param {number} cz Disc centre Z.
   * @param {number} range Disc radius.
   * @param {number} leashX Leash anchor X.
   * @param {number} leashZ Leash anchor Z.
   * @param {number} leashRange Max distance from the leash anchor.
   * @returns {void}
   * @private
   */
  _pickIdleDestination(agent, cx, cz, range, leashX, leashZ, leashRange) {
    const { brain } = agent;
    const margin = agent.entity.get(Collider)?.halfX ?? 0.5;

    for (let i = 0; i < IDLE_DESTINATION_TRIES; i++) {
      const angle = randomRange(0, TAU);
      const radius = range * Math.sqrt(Math.random());
      const x = cx + Math.cos(angle) * radius;
      const z = cz + Math.sin(angle) * radius;

      if (distanceSqXZ(x, z, leashX, leashZ) > leashRange * leashRange) continue;
      if (this._pointBlocked(x, z, margin)) continue;
      if (this._hasLineOfSight(agent.transform.position.x, agent.transform.position.z, x, z) === false) continue;

      brain.navTargetX = x;
      brain.navTargetZ = z;
      brain.hasNavTarget = true;
      return;
    }
    // No valid point this cycle: stay put until the next selection tick.
    brain.hasNavTarget = false;
  }

  /**
   * @param {import('../components/AIBrain.js').AIBrain} brain Agent brain.
   * @returns {void}
   * @private
   */
  _clearNav(brain) {
    brain.hasNavTarget = false;
    brain.path = null;
    brain.pathIndex = 0;
  }

  // ---------------------------------------------------------- world queries

  /**
   * Snapshots every static collider as an AABB for this frame's LOS/point tests.
   * @returns {void}
   * @private
   */
  _rebuildWalls() {
    this._wallCount = 0;
    for (const entity of this.world.query(Transform, Collider)) {
      const collider = entity.get(Collider);
      if (!collider.isStatic) continue;
      const t = entity.get(Transform);
      let box = this._walls[this._wallCount];
      if (!box) {
        box = { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
        this._walls[this._wallCount] = box;
      }
      box.minX = t.position.x - collider.halfX;
      box.maxX = t.position.x + collider.halfX;
      box.minZ = t.position.z - collider.halfZ;
      box.maxZ = t.position.z + collider.halfZ;
      this._wallCount++;
    }
  }

  /**
   * @param {number} ax Segment start X.
   * @param {number} az Segment start Z.
   * @param {number} bx Segment end X.
   * @param {number} bz Segment end Z.
   * @returns {boolean} True when no static collider blocks the segment.
   * @private
   */
  _hasLineOfSight(ax, az, bx, bz) {
    const dx = bx - ax;
    const dz = bz - az;
    for (let i = 0; i < this._wallCount; i++) {
      const box = this._walls[i];
      const t = raycastAABB(ax, az, dx, dz, box.minX, box.minZ, box.maxX, box.maxZ);
      if (t >= 0 && t <= 1) return false;
    }
    return true;
  }

  /**
   * @param {number} x Point X.
   * @param {number} z Point Z.
   * @param {number} margin Extra clearance kept around walls.
   * @returns {boolean} True when the point lies inside (or near) a static collider.
   * @private
   */
  _pointBlocked(x, z, margin) {
    for (let i = 0; i < this._wallCount; i++) {
      const box = this._walls[i];
      if (x >= box.minX - margin && x <= box.maxX + margin && z >= box.minZ - margin && z <= box.maxZ + margin) {
        return true;
      }
    }
    return false;
  }

  /**
   * @param {string} state FSM state name.
   * @returns {boolean} Whether this state manages its own body rotation.
   * @private
   */
  _usesManagedRotation(state) {
    return (
      state === AIState.AGGRO_CHARGE ||
      state === AIState.EXPLODING ||
      state === AIState.IDLE_STATIC ||
      state === AIState.IDLE_ROAMING ||
      state === AIState.IDLE_PATROL
    );
  }

  // ---------------------------------------------------------- detonation

  /**
   * Flags a bomber that took damage so its idle/charge logic reacts, and seeds
   * the last-known position from the damage point.
   * @param {{ target: import('../ecs/Entity.js').Entity }} payload Damage event.
   * @returns {void}
   * @private
   */
  _onDamage({ target }) {
    if (!target?.alive) return;
    const brain = target.get(AIBrain);
    if (!brain) return;

    // Any damage to a bomber arms it, even from outside its vision cone.
    if (target.has(ExplosiveTrigger)) {
      brain.wasDamaged = true;
      return;
    }

    // A hit also wakes a classic idle agent (issue #17): a shot from behind
    // or from outside its seek range still counts as a detection.
    if (brain.state !== AIState.IDLE && !IDLE_STATES.includes(brain.state)) return;
    if (target.get(Health)?.dead) return;
    this._transition(target, brain, AIState.SEEK);
  }

  /**
   * Death handler: applies the blast when the dead entity was a primed or
   * explode-on-death bomber. The blast itself is guarded to run exactly once.
   * @param {import('../ecs/Entity.js').Entity} entity Entity that died.
   * @returns {void}
   * @private
   */
  _onDied(entity) {
    const trigger = entity.get(ExplosiveTrigger);
    if (!trigger) return;
    if (trigger.hasStartedExploding || trigger.explodeOnDeath) this._detonate(entity);
  }

  /**
   * Applies one area blast: faction-filtered, optional falloff, optional
   * line-of-sight shielding and optional knockback. Idempotent via the
   * `hasAppliedDamage` flag, so contact and death can never double-hit.
   * @param {import('../ecs/Entity.js').Entity} entity Bomber entity.
   * @returns {void}
   * @private
   */
  _detonate(entity) {
    const trigger = entity.get(ExplosiveTrigger);
    const transform = entity.get(Transform);
    if (!trigger || !transform || trigger.hasAppliedDamage) return;
    trigger.hasAppliedDamage = true;

    const ox = transform.position.x;
    const oz = transform.position.z;
    const radiusSq = trigger.radius * trigger.radius;

    for (const victim of this.world.query(Health, Transform)) {
      if (victim === entity || !victim.alive) continue;

      const isPlayer = victim.has(PlayerControlled);
      if (isPlayer && !trigger.affectsPlayer) continue;
      if (!isPlayer && !trigger.affectsEnemies) continue;

      const vp = victim.get(Transform).position;
      const dx = vp.x - ox;
      const dz = vp.z - oz;
      const distSq = dx * dx + dz * dz;
      if (distSq > radiusSq) continue;

      if (trigger.requiresLineOfSight && !this._hasLineOfSight(ox, oz, vp.x, vp.z)) continue;

      const dist = Math.sqrt(distSq);
      const t = trigger.radius > 0 ? dist / trigger.radius : 0;
      const amount = trigger.falloffEnabled ? lerp(trigger.damage, trigger.minimumDamage, t) : trigger.damage;

      // The blast's shove rides on the damage rather than being applied here
      // (issue #75). Two things fall out of that: it goes through
      // `Velocity.knockback`, which steering cannot swallow the way it swallowed
      // a push written straight to `linear`; and it only lands when the damage
      // does, so a victim whose shield ate the hit is no longer thrown by it.
      this._blastEvent.target = victim;
      this._blastEvent.amount = amount;
      this._blastEvent.source = entity;
      this._blastEvent.point = transform.position;
      this._blastDirection.x = dx;
      this._blastDirection.z = dz;
      this._blastEvent.impulse = trigger.force > 0 ? trigger.force * (1 - t) : 0;
      this.ctx.events.emit(Events.DAMAGE, this._blastEvent);
    }

    this._announceExplosion(transform.position, trigger.radius);
  }

  /**
   * Fires the shared presentation events for a blast. Nothing here knows what
   * listens (mirrors {@link ExplosionSystem}).
   * @param {import('three').Vector3} position Blast centre.
   * @param {number} radius Blast radius.
   * @returns {void}
   * @private
   */
  _announceExplosion(position, radius) {
    const { events, config } = this.ctx;
    events.emit(Events.EXPLOSION, { position, radius, color: PALETTE.explosion });
    events.emit(Events.FX_BURST, {
      position,
      direction: { x: 0, y: 1, z: 0 },
      count: config.particles.explosionCount,
      options: { speed: [8, 26], spread: 1.1, life: [0.25, 0.7], size: [0.2, 0.5], color: PALETTE.explosion }
    });
    events.emit(Events.FLASH, { position, color: PALETTE.explosion, intensity: 22, life: 0.32 });
    events.emit(Events.SHAKE, { amount: config.feel.trauma.explosion });
  }

  /** @returns {import('../ecs/Entity.js').Entity | null} The live player, if any. */
  _findTarget() {
    const players = this.world.query(PlayerControlled, Transform, Health);
    for (const player of players) {
      if (!player.get(Health).dead) return player;
    }
    return null;
  }

  // ---------------------------------------------------------- classic helpers

  /**
   * Steers toward (or away from) the target.
   * @param {AgentContext} agent Current agent.
   * @param {number} sign 1 to approach, -1 to retreat.
   * @returns {void}
   * @private
   */
  _chase(agent, sign) {
    this._steerTo(agent, agent.targetTransform.position, sign);
  }

  /**
   * Steers toward (or away from) an arbitrary point, which is what lets an
   * agent walk to something that is not its target — a weapon on the floor.
   *
   * @param {AgentContext} agent Current agent.
   * @param {import('three').Vector3} to Destination.
   * @param {number} sign 1 to approach, -1 to retreat.
   * @returns {void}
   * @private
   */
  _steerTo(agent, to, sign) {
    this._steerToPoint(agent, to.x, to.z, sign);
  }

  /**
   * Steers toward (or away from) a scalar point at the agent's max speed.
   * @param {AgentContext} agent Current agent.
   * @param {number} x Destination X.
   * @param {number} z Destination Z.
   * @param {number} sign 1 to approach, -1 to retreat.
   * @returns {void}
   * @private
   */
  _steerToPoint(agent, x, z, sign) {
    const from = agent.transform.position;
    const dx = x - from.x;
    const dz = z - from.z;
    const length = Math.hypot(dx, dz) || 1;

    agent.velocity.desired.set(
      (dx / length) * agent.velocity.maxSpeed * sign,
      0,
      (dz / length) * agent.velocity.maxSpeed * sign
    );
  }

  /**
   * Ranged kiting: close in when too far, back off when too close, otherwise
   * circle the target so the agent never becomes a static turret.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _holdDistance(agent) {
    const { brain } = agent;
    const preferred = brain.preferredRange;

    if (agent.distance > preferred * 1.15) {
      this._approach(agent);
      return;
    }
    if (agent.distance < preferred * 0.85) {
      this._chase(agent, -1);
      return;
    }

    if (brain.timeInState % (STRAFE_FLIP_INTERVAL * 2) < agent.dt) brain.strafeSign *= -1;

    const to = agent.targetTransform.position;
    const from = agent.transform.position;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz) || 1;

    // Perpendicular to the line of sight.
    agent.velocity.desired.set(
      (-dz / length) * agent.velocity.maxSpeed * brain.strafeSign,
      0,
      (dx / length) * agent.velocity.maxSpeed * brain.strafeSign
    );
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _brake(agent) {
    agent.velocity.desired.set(0, 0, 0);
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @param {boolean} held Whether the agent's weapon should be firing.
   * @returns {void}
   * @private
   */
  _setTrigger(agent, held) {
    const weapon = agent.entity.get(Weapon);
    if (weapon) weapon.triggerHeld = held;
  }

  /**
   * Melee attack. Agents that carry a blast and deal no contact damage — the
   * classic exploders — spend themselves instead, which routes through the
   * normal death path and lets the explosion system do the rest.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _tryAttack(agent) {
    const { brain } = agent;
    if (brain.attackCooldown > 0 || !brain.target) return;

    if (brain.damage <= 0 && agent.entity.has(Explosive)) {
      const health = agent.entity.get(Health);
      this.ctx.events.emit(Events.DAMAGE, {
        target: agent.entity,
        amount: health ? health.max : 1,
        source: agent.entity,
        point: agent.transform.position
      });
      return;
    }

    brain.attackCooldown = 1 / brain.attackRate;
    brain.meleeSwingRemaining = brain.meleeSwingDuration;
    this.ctx.events.emit(Events.ENEMY_MELEE_ATTACK, {
      entity: agent.entity,
      target: brain.target,
      position: agent.targetTransform?.position ?? agent.transform.position,
      damage: brain.damage
    });
    this._emitMeleeHit(agent);
    this.ctx.events.emit(Events.DAMAGE, {
      target: brain.target,
      amount: brain.damage,
      source: agent.entity,
      point: agent.transform.position,
      type: 'melee',
      direction: this._meleeDirection(agent),
      impulse: this.ctx.config?.knockback?.contactImpulse ?? 0
    });
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {{ x: number, z: number }} Direction the melee impact pushes.
   * @private
   */
  _meleeDirection(agent) {
    const direction = this._meleeDamageDirection;
    if (!agent.targetTransform) {
      direction.x = Math.sin(agent.transform.rotationY);
      direction.z = Math.cos(agent.transform.rotationY);
      return direction;
    }

    direction.x = agent.targetTransform.position.x - agent.transform.position.x;
    direction.z = agent.targetTransform.position.z - agent.transform.position.z;
    if (direction.x * direction.x + direction.z * direction.z < 1e-6) {
      direction.x = Math.sin(agent.transform.rotationY);
      direction.z = Math.cos(agent.transform.rotationY);
    }
    return direction;
  }

  /**
   * Contact damage now reads as a deliberate swing instead of passive overlap:
   * a small forward burst marks the impact point without changing combat rules.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _emitMeleeHit(agent) {
    if (!agent.targetTransform) return;
    this._starPosition.copy(agent.targetTransform.position);
    this._starPosition.y += 0.8;

    const dx = agent.targetTransform.position.x - agent.transform.position.x;
    const dz = agent.targetTransform.position.z - agent.transform.position.z;
    const length = Math.hypot(dx, dz) || 1;
    this._starDirection.set(dx / length, 0.28, dz / length);

    this.ctx.events.emit(Events.FX_BURST, {
      position: this._starPosition,
      direction: this._starDirection,
      count: 7,
      options: {
        speed: [1.4, 3.8],
        spread: 0.45,
        life: [0.1, 0.22],
        size: [0.08, 0.16],
        color: PALETTE.enemyCore
      }
    });
  }

  /**
   * Pushes nearby agents apart so a swarm spreads out instead of stacking into
   * a single column behind the player.
   * @param {AgentContext} agent Current agent.
   * @param {import('../ecs/Entity.js').Entity[]} agents All agents this frame.
   * @returns {void}
   * @private
   */
  _applySeparation(agent, agents) {
    const { brain, transform, velocity } = agent;
    const radius = brain.separationRadius;
    const radiusSq = radius * radius;

    let pushX = 0;
    let pushZ = 0;

    for (const other of agents) {
      if (other === agent.entity) continue;
      const otherTransform = other.get(Transform);
      const dx = transform.position.x - otherTransform.position.x;
      const dz = transform.position.z - otherTransform.position.z;
      const distanceSq = dx * dx + dz * dz;
      if (distanceSq > radiusSq || distanceSq < 1e-6) continue;

      // Linear falloff: closest neighbours push hardest.
      const strength = 1 - Math.sqrt(distanceSq) / radius;
      const inverse = strength / Math.sqrt(distanceSq);
      pushX += dx * inverse;
      pushZ += dz * inverse;
    }

    if (pushX === 0 && pushZ === 0) return;
    velocity.desired.x += pushX * brain.separationStrength;
    velocity.desired.z += pushZ * brain.separationStrength;
  }

  // ------------------------------------------------------- scavenging (#15)

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when the agent is unarmed and something on the
   *   floor is worth walking to.
   * @private
   */
  _needsWeapon(agent) {
    if (agent.entity.has(Weapon)) return false;
    return this._claimWeapon(agent) !== null;
  }

  /**
   * Picks the nearest loaded weapon lying within the agent's search radius and
   * stakes a claim on it, so a pack does not converge on the same one. Claims
   * are strictly nearest-wins: a closer agent takes the claim off a further one,
   * which then falls back to whatever else is around.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {import('../ecs/Entity.js').Entity | null} The claimed weapon entity.
   * @private
   */
  _claimWeapon(agent) {
    const drops = this.world.query(ThrownWeapon, Weapon, Transform);
    if (drops.length === 0) return null;

    const range = agent.brain.weaponSearchRange;
    const rangeSq = range * range;
    let bestSq = Infinity;
    /** @type {import('../ecs/Entity.js').Entity | null} */
    let best = null;

    for (const drop of drops) {
      if (!drop.alive) continue;
      const marker = drop.get(ThrownWeapon);
      // Still in the air: it is a hazard, not a supply.
      if (!marker.grounded) continue;
      if (!drop.get(Weapon).isLoaded) continue;

      const position = drop.get(Transform).position;
      const distanceSq = distanceSqXZ(
        agent.transform.position.x,
        agent.transform.position.z,
        position.x,
        position.z
      );
      if (distanceSq > rangeSq || distanceSq >= bestSq) continue;
      if (!this._canClaim(marker, agent, position, distanceSq)) continue;

      bestSq = distanceSq;
      best = drop;
    }

    if (best) best.get(ThrownWeapon).claimedBy = agent.entity.id;
    return best;
  }

  /**
   * @param {import('../components/ThrownWeapon.js').ThrownWeapon} marker Weapon's floor state.
   * @param {AgentContext} agent Agent that wants it.
   * @param {import('three').Vector3} position Where the weapon lies.
   * @param {number} distanceSq Agent's squared distance to it.
   * @returns {boolean} True when the agent may take the claim.
   * @private
   */
  _canClaim(marker, agent, position, distanceSq) {
    if (marker.claimedBy === 0 || marker.claimedBy === agent.entity.id) return true;

    const holder = this.world.getEntity(marker.claimedBy);
    // A claim only survives while its owner is alive, still unarmed and still
    // able to walk; otherwise it lapses and anyone may take over.
    if (!holder || !holder.alive || !holder.has(AIBrain) || holder.has(Weapon)) return true;
    const holderBrain = holder.get(AIBrain);
    if (holderBrain.stunRemaining > 0 || holderBrain.state === AIState.DEAD) return true;

    const holderPosition = holder.get(Transform).position;
    return (
      distanceSq <
      distanceSqXZ(holderPosition.x, holderPosition.z, position.x, position.z)
    );
  }

  /**
   * Moves the weapon component itself onto the agent — no clone, so the stats
   * the player threw away are exactly the stats now pointed back at them.
   *
   * @param {AgentContext} agent Agent doing the picking up.
   * @param {import('../ecs/Entity.js').Entity} drop Weapon entity on the floor.
   * @returns {void}
   * @private
   */
  _equip(agent, drop) {
    const weapon = drop.get(Weapon);
    drop.remove(Weapon);
    weapon.triggerHeld = false;
    weapon.cooldown = 0;

    agent.entity.add(weapon);
    agent.brain.armWith(weapon);

    const position = drop.get(Transform).position;
    this._starPosition.copy(position);
    this._starDirection.set(0, 1, 0);
    this.ctx.events.emit(Events.FX_BURST, {
      position: this._starPosition,
      direction: this._starDirection,
      count: 8,
      options: {
        speed: [2, 4],
        spread: 0.7,
        life: [0.2, 0.34],
        size: [0.08, 0.16],
        color: PALETTE.pickupWeapon
      }
    });

    this.world.destroyEntity(drop);
  }

  /**
   * Ring of stars orbiting a stunned enemy's head. Each star is its own
   * one-particle burst placed on the ring, which is what makes them read as
   * circling rather than as one more impact puff; the ring angle advances every
   * emission so the whole crown turns.
   *
   * @param {AgentContext} agent Stunned agent.
   * @returns {void}
   * @private
   */
  _emitStunStars(agent) {
    const { brain } = agent;
    const stun = this.ctx.config.throwing;

    brain.stunFxCooldown -= agent.dt;
    if (brain.stunFxCooldown > 0) return;
    brain.stunFxCooldown = stun.stunStarInterval;
    brain.stunSpin += stun.stunStarSpin;

    const step = (Math.PI * 2) / stun.stunStarCount;
    for (let index = 0; index < stun.stunStarCount; index++) {
      const angle = brain.stunSpin + index * step;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      this._starPosition.set(
        agent.transform.position.x + cos * stun.stunStarRadius,
        agent.transform.position.y + stun.stunStarRadius * 1.6,
        agent.transform.position.z + sin * stun.stunStarRadius
      );
      // Tangential, with enough lift to hold height against particle gravity.
      this._starDirection.set(-sin, 0.85, cos);
      this.ctx.events.emit(Events.FX_BURST, this._starEvent);
    }
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _faceTarget(agent) {
    if (!agent.targetTransform) return;
    const dx = agent.targetTransform.position.x - agent.transform.position.x;
    const dz = agent.targetTransform.position.z - agent.transform.position.z;
    if (dx * dx + dz * dz < 1e-6) return;

    agent.transform.rotationY = dampAngle(
      agent.transform.rotationY,
      Math.atan2(dx, dz),
      TURN_RATE * 2,
      agent.dt
    );
  }

  /**
   * Turns the body toward where it is heading, or toward the target when still.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _faceMotion(agent) {
    const { velocity, transform, targetTransform } = agent;
    let dx = velocity.desired.x;
    let dz = velocity.desired.z;

    if (dx * dx + dz * dz < 1e-4) {
      if (!targetTransform) return;
      dx = targetTransform.position.x - transform.position.x;
      dz = targetTransform.position.z - transform.position.z;
      if (dx * dx + dz * dz < 1e-6) return;
    }

    transform.rotationY = dampAngle(transform.rotationY, Math.atan2(dx, dz), TURN_RATE, agent.dt);
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    this._walls.length = 0;
    this._wallCount = 0;
  }
}
